import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Alert,
  FormHelperText,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Category as CategoryIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { extractTaskFields } from '../../../api/task.api.js';

function TaskForm({
  task, onSave, onCancel, loading = false,
}) {
  const [formData, setFormData] = useState({
    title: '',
    dueDate: null,
    type: '',
    status: 'Pending',
    related: '',
    priority: 'Medium',
    assignedTo: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [nlpProvider, setNlpProvider] = useState('openai');
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        dueDate: task.dueDate ? new Date(task.dueDate) : null,
        type: task.type || '',
        status: task.status || 'Pending',
        related: task.related || '',
        priority: task.priority || 'Medium',
        assignedTo: task.assignedTo || '',
        notes: task.notes || '',
      });
    }
  }, [task]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    return newErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const taskData = {
      ...formData,
      dueDate: formData.dueDate ? formData.dueDate.toISOString() : null,
    };

    onSave(taskData);
  };

  const startListening = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        setTranscript('');
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        setTranscript(finalTranscript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } else {
      alert('Speech recognition is not supported in this browser.');
    }
  };

  const stopListening = () => {
    if ('webkitSpeechRecognition' in window) {
      window.webkitSpeechRecognition.abort();
    }
  };

  const handleExtractFields = async () => {
    if (!transcript.trim()) {
      alert('Please provide some text to extract fields from.');
      return;
    }

    setExtracting(true);
    try {
      const result = await extractTaskFields(transcript, nlpProvider);
      if (result.success && result.fields) {
        const extractedData = { ...formData };

        if (result.fields.title) extractedData.title = result.fields.title;
        if (result.fields.dueDate) {
          const date = new Date(result.fields.dueDate);
          if (!isNaN(date.getTime())) {
            extractedData.dueDate = date;
          }
        }
        if (result.fields.type) extractedData.type = result.fields.type;
        if (result.fields.status) extractedData.status = result.fields.status;
        if (result.fields.priority) extractedData.priority = result.fields.priority;
        if (result.fields.assignedTo) extractedData.assignedTo = result.fields.assignedTo;
        if (result.fields.notes) extractedData.notes = result.fields.notes;

        setFormData(extractedData);
        setTranscript('');
        alert('Fields extracted successfully!');
      }
    } catch (error) {
      console.error('Error extracting fields:', error);
      alert('Failed to extract fields. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <Stack spacing={3} sx={{ mt: 1 }}>
        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <AssignmentIcon color="primary" />
              <Typography variant="h6">
                {task ? 'Edit Task' : 'Add New Task'}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Task Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                required
              />

              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    <MenuItem value="Call">Call</MenuItem>
                    <MenuItem value="Meeting">Meeting</MenuItem>
                    <MenuItem value="Email">Email</MenuItem>
                    <MenuItem value="FollowUp">Follow Up</MenuItem>
                    <MenuItem value="Research">Research</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                    <MenuItem value="On Hold">On Hold</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Due Date"
                    value={formData.dueDate}
                    onChange={(newValue) => handleInputChange('dueDate', newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Stack>

              <TextField
                fullWidth
                label="Assigned To"
                value={formData.assignedTo}
                onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                placeholder="Enter assignee name"
              />

              <TextField
                fullWidth
                label="Related To"
                value={formData.related}
                onChange={(e) => handleInputChange('related', e.target.value)}
                placeholder="Contact, opportunity, or project"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Voice Input & NLP */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <MicIcon color="primary" />
              <Typography variant="h6">Voice Input & AI Extraction</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Alert severity="info">
                Use voice input to quickly create tasks. The AI will extract relevant fields from your speech.
              </Alert>

              <FormControl fullWidth>
                <InputLabel>NLP Provider</InputLabel>
                <Select
                  value={nlpProvider}
                  label="NLP Provider"
                  onChange={(e) => setNlpProvider(e.target.value)}
                >
                  <MenuItem value="openai">OpenAI GPT</MenuItem>
                  <MenuItem value="llama4">Llama 4 (Together AI)</MenuItem>
                  <MenuItem value="ollama">Ollama (Local)</MenuItem>
                </Select>
                <FormHelperText>
                  Choose the AI model for field extraction
                </FormHelperText>
              </FormControl>

              <Box>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <IconButton
                    onClick={isListening ? stopListening : startListening}
                    color={isListening ? 'error' : 'primary'}
                    size="large"
                  >
                    {isListening ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {isListening ? 'Listening... Click to stop' : 'Click to start voice input'}
                  </Typography>
                </Stack>

                {transcript && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Voice Transcript"
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}

                {transcript && (
                  <Button
                    variant="outlined"
                    onClick={handleExtractFields}
                    disabled={extracting}
                    startIcon={<AssignmentIcon />}
                  >
                    {extracting ? 'Extracting...' : 'Extract Fields'}
                  </Button>
                )}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Notes */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
              <CategoryIcon color="primary" />
              <Typography variant="h6">Notes & Details</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Add detailed notes about this task..."
            />
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3,
        }}
        >
          <Button onClick={onCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : (task ? 'Update' : 'Save')}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}

export default TaskForm;
